import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api'
import axios from 'axios'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputNumber } from 'primereact/inputnumber'
import { InputTextarea } from 'primereact/inputtextarea'
import { Toast } from 'primereact/toast'
import { useAuth } from '../contexts/AuthContext'
import { Member } from 'shared'

const PendingMembersWorkflow: React.FC = () => {
  const [pendingMembers, setPendingMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showDeclineDialog, setShowDeclineDialog] = useState(false)
  const [customFee, setCustomFee] = useState<number | null>(null)
  const [declineReason, setDeclineReason] = useState('')
  const [defaultFee, setDefaultFee] = useState<number>(12.00)
  const [processing, setProcessing] = useState(false)
  const toast = React.useRef<Toast>(null)
  const { logout } = useAuth()


  useEffect(() => {
    fetchPendingMembers()
    fetchDefaultFee()
  }, [])

  const fetchPendingMembers = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await axios.get(`${API_BASE_URL}/api/members/pending`)
      setPendingMembers(response.data.data?.members || [])
    } catch (err: any) {
      console.error('Failed to fetch pending members:', err)
      if (err.response?.status === 401) {
        logout()
      } else {
        setError(err.response?.data?.message || 'Failed to load pending members')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchDefaultFee = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings`)
      const settings = response.data.data?.settings || {}
      setDefaultFee(settings.DEFAULT_MEMBERSHIP_FEE || 12.00)
    } catch (err: any) {
      console.error('Failed to fetch default fee:', err)
    }
  }

  const handleApprove = async () => {
    if (!selectedMember) return

    try {
      setProcessing(true)
      const payload: any = {}
      if (customFee !== null) {
        payload.membershipFee = customFee
      }

      await axios.post(`${API_BASE_URL}/api/members/${selectedMember.id}/approve`, payload)

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Member ${selectedMember.firstName} ${selectedMember.lastName} has been approved`,
        life: 3000
      })

      // Refresh the list
      await fetchPendingMembers()

      // Close dialog and reset state
      setShowApproveDialog(false)
      setSelectedMember(null)
      setCustomFee(null)
    } catch (err: any) {
      console.error('Failed to approve member:', err)
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to approve member',
        life: 5000
      })
    } finally {
      setProcessing(false)
    }
  }

  const handleDecline = async () => {
    if (!selectedMember) return

    try {
      setProcessing(true)

      await axios.post(`${API_BASE_URL}/api/members/${selectedMember.id}/decline`, {
        reason: declineReason
      })

      toast.current?.show({
        severity: 'info',
        summary: 'Member Declined',
        detail: `Member ${selectedMember.firstName} ${selectedMember.lastName} has been declined`,
        life: 3000
      })

      // Refresh the list
      await fetchPendingMembers()

      // Close dialog and reset state
      setShowDeclineDialog(false)
      setSelectedMember(null)
      setDeclineReason('')
    } catch (err: any) {
      console.error('Failed to decline member:', err)
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to decline member',
        life: 5000
      })
    } finally {
      setProcessing(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE')
  }

  const nameBodyTemplate = (member: Member) => {
    return (
      <div>
        <div className="font-semibold">{member.firstName} {member.lastName}</div>
        <div className="text-sm text-gray-500">#{member.memberNumber}</div>
      </div>
    )
  }

  const contactBodyTemplate = (member: Member) => {
    return (
      <div>
        <div className="text-sm">{member.email}</div>
        {member.phone && <div className="text-sm text-gray-500">{member.phone}</div>}
      </div>
    )
  }

  const addressBodyTemplate = (member: Member) => {
    if (!member.address) return '-'
    return (
      <div className="text-sm">
        <div>{member.address.street1}</div>
        <div>{member.address.city} {member.address.zipCode}</div>
      </div>
    )
  }

  const dateBodyTemplate = (member: Member) => {
    return (
      <div className="text-sm">
        <div>Registered: {formatDate(member.createdAt)}</div>
        {member.dateOfBirth && <div>Born: {formatDate(member.dateOfBirth)}</div>}
      </div>
    )
  }

  const actionsBodyTemplate = (member: Member) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-check"
          size="small"
          className="p-button-success p-button-outlined"
          tooltip="Approve Member"
          onClick={() => {
            setSelectedMember(member)
            setShowApproveDialog(true)
          }}
        />
        <Button
          icon="pi pi-times"
          size="small"
          className="p-button-danger p-button-outlined"
          tooltip="Decline Member"
          onClick={() => {
            setSelectedMember(member)
            setShowDeclineDialog(true)
          }}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <div className="text-center p-6">
            <i className="pi pi-exclamation-triangle text-6xl text-red-500 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Pending Members</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button
              label="Try Again"
              icon="pi pi-refresh"
              onClick={fetchPendingMembers}
              className="p-button-outlined"
            />
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <Toast ref={toast} />
      <Card>
        <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Pending Member Approvals</h2>
          <Button
            icon="pi pi-refresh"
            onClick={fetchPendingMembers}
            className="p-button-outlined p-button-sm"
            loading={loading}
            tooltip="Refresh"
            tooltipOptions={{ position: 'bottom' }}
          />
        </div>

        {pendingMembers.length === 0 ? (
          <div className="text-center p-8">
            <i className="pi pi-users text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Pending Members</h3>
            <p className="text-gray-500">All registration requests have been processed.</p>
          </div>
        ) : (
          <DataTable
            value={pendingMembers}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            className="p-datatable-sm"
            emptyMessage="No pending members found"
            showGridlines
            stripedRows
            scrollable
            scrollHeight="600px"
          >
            <Column
              header="Actions"
              body={actionsBodyTemplate}
              style={{ minWidth: '80px', width: 'auto' }}
            />
            <Column
              field="name"
              header="Member"
              body={nameBodyTemplate}
              sortable
            />
            <Column
              field="contact"
              header="Contact"
              body={contactBodyTemplate}
            />
            <Column
              field="address"
              header="Address"
              body={addressBodyTemplate}
            />
            <Column
              field="dates"
              header="Registration"
              body={dateBodyTemplate}
            />
            <Column
              field="notes"
              header="Notes"
              body={(member) => member.notes || '-'}
              style={{ maxWidth: '200px' }}
            />
          </DataTable>
        )}
      </Card>

      {/* Approve Dialog */}
      <Dialog
        header={`Approve Member: ${selectedMember?.firstName} ${selectedMember?.lastName}`}
        visible={showApproveDialog}
        style={{ width: 'min(95vw, 450px)' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setShowApproveDialog(false)
                setSelectedMember(null)
                setCustomFee(null)
              }}
              className="p-button-text"
            />
            <Button
              label="Approve"
              icon="pi pi-check"
              onClick={handleApprove}
              loading={processing}
              className="p-button-success"
            />
          </div>
        }
        onHide={() => {
          if (!processing) {
            setShowApproveDialog(false)
            setSelectedMember(null)
            setCustomFee(null)
          }
        }}
      >
        <div className="space-y-4">
          <p>
            Are you sure you want to approve <strong>{selectedMember?.firstName} {selectedMember?.lastName}</strong>?
            This will activate their membership.
          </p>

          <div>
            <label htmlFor="membershipFee" className="block text-sm font-medium text-gray-700 mb-2">
              Membership Fee (€)
            </label>
            <InputNumber
              id="membershipFee"
              value={customFee}
              onValueChange={(e) => setCustomFee(e.value || null)}
              placeholder={`Default: €${defaultFee.toFixed(2)}`}
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={2}
              min={0}
              className="w-full"
            />
            <small className="text-gray-500">Leave empty to use default fee (€{defaultFee.toFixed(2)})</small>
          </div>
        </div>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog
        header={`Decline Member: ${selectedMember?.firstName} ${selectedMember?.lastName}`}
        visible={showDeclineDialog}
        style={{ width: 'min(95vw, 450px)' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setShowDeclineDialog(false)
                setSelectedMember(null)
                setDeclineReason('')
              }}
              className="p-button-text"
            />
            <Button
              label="Decline"
              icon="pi pi-times"
              onClick={handleDecline}
              loading={processing}
              className="p-button-danger"
            />
          </div>
        }
        onHide={() => {
          if (!processing) {
            setShowDeclineDialog(false)
            setSelectedMember(null)
            setDeclineReason('')
          }
        }}
      >
        <div className="space-y-4">
          <p>
            Are you sure you want to decline <strong>{selectedMember?.firstName} {selectedMember?.lastName}</strong>?
            This action cannot be undone.
          </p>

          <div>
            <label htmlFor="declineReason" className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Decline (Optional)
            </label>
            <InputTextarea
              id="declineReason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={3}
              className="w-full"
              placeholder="Enter reason for declining this member..."
            />
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default PendingMembersWorkflow